import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const links = [
  { href: "#work", label: "Work", key: "linkWork" },
  { href: "#process", label: "Process", key: "linkProcess" },
  { href: "#pricing", label: "Pricing", key: "linkPricing" },
  { href: "#faq", label: "FAQ", key: "linkFaq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid={TEST_IDS.nav.root}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1], delay: 0.05 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-[#FDFCFB]/75 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a
            href="#top"
            data-testid={TEST_IDS.nav.logo}
            className="flex items-center gap-2 group"
          >
            <span className="w-8 h-8 rounded-full bg-[#1C1C1A] text-[#FDFCFB] grid place-items-center font-serif-display text-[15px] leading-none">
              <span className="translate-y-[1px]">C</span>
            </span>
            <span className="font-serif-display text-[18px] tracking-tight">
              Church Clips <span className="italic text-[#787672]">Studio</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={TEST_IDS.nav[l.key]}
                className="link-underline text-[13px] tracking-[0.14em] uppercase text-[#1C1C1A] font-medium"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#book"
              data-testid={TEST_IDS.nav.ctaBook}
              className="hidden sm:inline-flex btn-pill btn-primary text-[13px] tracking-wide whitespace-nowrap"
            >
              Contact us
              <span aria-hidden="true">↗</span>
            </a>
            <button
              aria-label="Toggle menu"
              className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-black/10"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#FDFCFB] md:hidden pt-24 px-6"
            onClick={() => setOpen(false)}
          >
            <nav className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="font-serif-display text-4xl tracking-tight text-[#1C1C1A]"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href="#book"
                className="btn-pill btn-primary self-start mt-4"
                data-testid={`${TEST_IDS.nav.ctaBook}-mobile`}
              >
                Get in touch
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
